/**
 * KpiStatCard — a metric card for the admin dashboard (P0 kit): a micro-label,
 * a large tabular-nums value, an optional trend delta (colored by whether the
 * move is "good"), an optional sparkline (tiny Recharts line), and an optional
 * status pill. Builds on @garden/ui Card + GPill + StatTile semantics. Delta
 * color flows from --g-success / --g-error (never color-only — the direction
 * also shows an arrow glyph + an aria label). Design: _shared/KIT-SPEC.md.
 */
import { type ReactElement, type ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { Card, GPill, type GPillTone } from "@garden/ui";
import { CHART } from "@/features/admin/_shared/chartTokens";

export type KpiDirection = "up" | "down" | "flat";
export interface KpiDelta {
  value: number;
  direction: KpiDirection;
  /** Override the auto label (defaults to a translated up/down/flat word). */
  label?: string;
  /** Which direction is "good". Defaults to "up". */
  goodDirection?: "up" | "down";
}
export interface KpiStatus {
  tone: GPillTone;
  label: string;
}
export interface KpiStatCardProps {
  label: string;
  value: ReactNode;
  delta?: KpiDelta;
  sparkline?: readonly number[];
  status?: KpiStatus;
  /** Accessible suffix for a positive/negative move. */
  deltaAriaUp?: string;
  deltaAriaDown?: string;
  testid?: string;
}

/**
 * Delta color as a token-arbitrary Tailwind class (static literals so the JIT
 * scanner can see them). Returns a `--g-*` var class — never a hardcoded color.
 */
function deltaColorClass(delta: KpiDelta): string {
  if (delta.direction === "flat") return "text-[var(--g-ink-2)]";
  const good = delta.goodDirection ?? "up";
  return delta.direction === good ? "text-[var(--g-success)]" : "text-[var(--g-error)]";
}

export function KpiStatCard({
  label,
  value,
  delta,
  sparkline,
  status,
  deltaAriaUp,
  deltaAriaDown,
  testid = "kpi",
}: KpiStatCardProps): ReactElement {
  const DeltaIcon = delta
    ? delta.direction === "up"
      ? ArrowUpRight
      : delta.direction === "down"
        ? ArrowDownRight
        : Minus
    : null;
  const sparkData = sparkline?.map((y, i) => ({ i, y })) ?? [];

  return (
    <Card padding={16}>
      <div data-testid={testid} className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.04em] text-[var(--g-secondary)]">
            {label}
          </span>
          {status ? (
            <span data-testid={`${testid}-status`}>
              <GPill tone={status.tone} size="sm">
                {status.label}
              </GPill>
            </span>
          ) : null}
        </div>

        <div className="flex items-end justify-between gap-3">
          <span
            data-testid={`${testid}-value`}
            className="text-[26px] font-bold leading-none tracking-[-0.02em] tabular-nums text-foreground"
          >
            {value}
          </span>
          {sparkline && sparkline.length > 0 ? (
            <div data-testid={`${testid}-sparkline`} aria-hidden className="h-8 w-20 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparkData}>
                  <Line
                    type="monotone"
                    dataKey="y"
                    stroke={CHART.primary}
                    strokeWidth={1.75}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </div>

        {delta && DeltaIcon ? (
          <span
            data-testid={`${testid}-delta`}
            className={`mt-0.5 inline-flex items-center gap-1 text-xs font-medium tabular-nums ${deltaColorClass(delta)}`}
          >
            <DeltaIcon size={14} strokeWidth={2} aria-hidden />
            {Math.abs(delta.value)}%
            <span className="sr-only">
              {delta.direction === "up"
                ? (deltaAriaUp ?? "up")
                : delta.direction === "down"
                  ? (deltaAriaDown ?? "down")
                  : ""}
            </span>
            {delta.label ? <span className="text-[var(--g-secondary)]">{delta.label}</span> : null}
          </span>
        ) : null}
      </div>
    </Card>
  );
}

export default KpiStatCard;
